import React, { useEffect, useRef, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Label,
} from 'recharts';

// Options chart data is proxied via Vercel serverless function at /api/options-chart

// Parse OCC options contract symbol: AAPL250117C00150000
const OCC_RE = /^([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/;
function parseOCC(raw) {
  if (!raw) return null;
  const s = raw.toUpperCase().replace(/\s+/g, '');
  const m = s.match(OCC_RE);
  if (!m) return null;
  const [, ticker, yy, mm, dd, type, strikePad] = m;
  const strike = parseInt(strikePad, 10) / 1000;
  return { ticker, expiry: `20${yy}-${mm}-${dd}`, type: type === 'C' ? 'Call' : 'Put', strike, raw: s };
}

// ── TradingView underlying chart ──────────────────────────────────────────
function TradingViewChart({ symbol, height = 700, interval = '60', themeMode = 'night', C }) {
  const hostRef = useRef(null);
  const containerIdRef = useRef(`tv_replay_${Math.random().toString(36).slice(2, 9)}`);
  useEffect(() => {
    if (!hostRef.current) return;
    hostRef.current.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'tradingview-widget-container__widget';
    container.style.height = `${height}px`;
    container.style.width = '100%';
    const link = document.createElement('a');
    link.href = `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(symbol)}`;
    link.rel = 'noopener nofollow'; link.target = '_blank';
    link.textContent = 'Open in TradingView ↗';
    link.style.cssText = `color:${C.sub};font-size:11px;`;
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript'; script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: false, width: '100%', height, symbol, interval,
      timezone: 'America/Toronto', theme: themeMode === 'day' ? 'light' : 'dark',
      style: '1', locale: 'en', allow_symbol_change: true,
      support_host: 'https://www.tradingview.com',
      withdateranges: true, save_image: true, details: true,
      studies: ['Volume@tv-basicstudies', 'VWAP@tv-basicstudies'],
      container_id: containerIdRef.current,
    });
    container.id = containerIdRef.current;
    hostRef.current.appendChild(container);
    hostRef.current.appendChild(link);
    hostRef.current.appendChild(script);
  }, [symbol, height, interval, themeMode, C]);
  return <div ref={hostRef} style={{ height: `${height + 24}px`, width: '100%' }} />;
}

// ── Options price chart (recharts) ────────────────────────────────────────
function OptionsChart({ points, entry, exit, stop, target, C, height }) {
  if (!points || points.length === 0) return null;
  const closes = points.map(p => p.close);
  const minClose = Math.min(...closes);
  const maxClose = Math.max(...closes);
  const pad = (maxClose - minClose) * 0.15 || 0.1;
  const domainMin = Math.max(0, minClose - pad);
  const domainMax = maxClose + pad;

  const levels = [
    entry != null ? { val: entry, color: '#3b82f6', label: `Entry $${entry.toFixed(2)}` } : null,
    exit  != null ? { val: exit,  color: '#10b981', label: `Exit $${exit.toFixed(2)}`   } : null,
    stop  != null ? { val: stop,  color: '#ef4444', label: `Stop $${stop.toFixed(2)}`   } : null,
    target!= null ? { val: target,color: '#f59e0b', label: `Target $${target.toFixed(2)}`} : null,
  ].filter(Boolean);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="optGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}    />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
        <XAxis dataKey="time" stroke={C.sub} tick={{ fontSize: 8 }} interval="preserveStartEnd" />
        <YAxis stroke={C.sub} tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`}
          domain={[domainMin, domainMax]} width={58} />
        <Tooltip
          contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.text }}
          formatter={v => [`$${v}`, 'Price']}
        />
        <Area type="monotone" dataKey="close" stroke="#3b82f6" strokeWidth={2}
          fill="url(#optGrad)" dot={false} />
        {levels.map(l => (
          <ReferenceLine key={l.label} y={l.val} stroke={l.color} strokeWidth={1.5} strokeDasharray="4 3">
            <Label value={l.label} position="insideTopRight"
              style={{ fontSize: 10, fontWeight: 700, fill: l.color }} />
          </ReferenceLine>
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Trade execution overlay (for TradingView underlying chart) ────────────
function TradeExecutionOverlay({ trade, C }) {
  const entry = parseFloat(trade?.entry);
  const exit  = parseFloat(trade?.exit);
  const stop  = parseFloat(trade?.stopLoss);
  const target= parseFloat(trade?.target);
  const levels = [
    Number.isFinite(entry)  ? { key:'entry', label:`Entry $${entry.toFixed(2)}`,   value:entry,  color:C.blueBright } : null,
    Number.isFinite(exit)   ? { key:'exit',  label:`Exit $${exit.toFixed(2)}`,     value:exit,   color:C.greenBright } : null,
    Number.isFinite(stop)   ? { key:'stop',  label:`Stop $${stop.toFixed(2)}`,     value:stop,   color:C.redBright } : null,
    Number.isFinite(target) ? { key:'target',label:`Target $${target.toFixed(2)}`, value:target, color:C.orange } : null,
  ].filter(Boolean);
  if (levels.length === 0) return null;
  const minV = Math.min(...levels.map(l => l.value));
  const maxV = Math.max(...levels.map(l => l.value));
  const range = (maxV - minV) || 1;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {levels.map(l => {
        const top = 56 + ((maxV - l.value) / range) * 62;
        return (
          <div key={l.key} style={{ position: 'absolute', left: 12, right: 12, top: `${top}%` }}>
            <div style={{ height: '1px', background: `linear-gradient(90deg,${l.color}bb,${l.color}22)` }} />
            <div style={{ position:'absolute',right:0,top:'-10px',background:'#0b1424ee',border:`1px solid ${l.color}66`,color:l.color,borderRadius:10,padding:'2px 8px',fontSize:10,fontWeight:700 }}>
              {l.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Options Chain Table ───────────────────────────────────────────────────
function OptionsChain({ ticker, currentOcc, onSelectContract, C, inp }) {
  const [chainMeta, setChainMeta] = useState(null);   // { expirationDates, price }
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [chain, setChain] = useState(null);            // { calls, puts, price }
  const [chainLoading, setChainLoading] = useState(false);
  const [chainError, setChainError] = useState(null);
  const [showType, setShowType] = useState('calls');   // 'calls' | 'puts' | 'both'

  // Load available expiry dates on mount / ticker change
  useEffect(() => {
    if (!ticker) return;
    setChainMeta(null); setChain(null); setSelectedExpiry(null); setChainError(null);
    fetch(`/api/news?type=chain&ticker=${encodeURIComponent(ticker)}`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setChainMeta(d); setSelectedExpiry(d.expirationDates?.[0]?.value || null); })
      .catch(e => setChainError(e.message));
  }, [ticker]);

  // Load chain when expiry selected
  useEffect(() => {
    if (!ticker || !selectedExpiry) return;
    setChainLoading(true); setChain(null); setChainError(null);
    const ts = new Date(selectedExpiry).getTime();
    fetch(`/api/news?type=chain&ticker=${encodeURIComponent(ticker)}&expiry=${ts}`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setChain(d); })
      .catch(e => setChainError(e.message))
      .finally(() => setChainLoading(false));
  }, [ticker, selectedExpiry]);

  const price = chain?.price || chainMeta?.price;
  const atmStrike = price && chain ? chain.calls.reduce((closest, c) =>
    Math.abs(c.strike - price) < Math.abs(closest.strike - price) ? c : closest,
    chain.calls[0] || { strike: 0 }
  )?.strike : null;

  const colHead = { color: C.sub, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '4px 6px', whiteSpace: 'nowrap' };
  const cell = (val, color, mono) => ({ color: color || C.text, fontSize: 11, padding: '3px 6px', fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right', whiteSpace: 'nowrap' });

  const buildOCC = (sym) => sym; // sym IS the OCC symbol from Yahoo

  return (
    <div style={{ marginTop: 4 }}>
      {/* Controls row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        {price && <div style={{ padding: '4px 10px', borderRadius: 6, background: `${C.blue}18`, border: `1px solid ${C.blue}44`, color: C.blueBright, fontSize: 12, fontWeight: 800 }}>{ticker} ${price.toFixed(2)}</div>}
        {chainMeta?.expirationDates?.length > 0 && (
          <select value={selectedExpiry || ''} onChange={e => setSelectedExpiry(e.target.value)} style={{ ...inp, width: 140, padding: '5px 8px', fontSize: 12 }}>
            {chainMeta.expirationDates.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', borderRadius: 6, border: `1px solid ${C.border}`, overflow: 'hidden', marginLeft: 'auto' }}>
          {[['calls','Calls'],['puts','Puts'],['both','Both']].map(([v,l]) => (
            <button key={v} onClick={() => setShowType(v)} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: showType === v ? C.blue : 'transparent', color: showType === v ? '#fff' : C.sub }}>{l}</button>
          ))}
        </div>
      </div>

      {chainError && <div style={{ color: C.redBright, fontSize: 12, padding: 8 }}>⚠ {chainError}</div>}
      {chainLoading && <div style={{ color: C.sub, fontSize: 12, padding: 12, textAlign: 'center' }}>Loading chain…</div>}

      {chain && !chainLoading && (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.card2, borderBottom: `1px solid ${C.border}` }}>
                {/* Calls side */}
                {showType !== 'puts' && <>
                  <th style={{ ...colHead, color: C.greenBright }}>Vol</th>
                  <th style={{ ...colHead, color: C.greenBright }}>OI</th>
                  <th style={{ ...colHead, color: C.greenBright }}>IV%</th>
                  <th style={{ ...colHead, color: C.greenBright }}>Ask</th>
                  <th style={{ ...colHead, color: C.greenBright }}>Bid</th>
                </>}
                {/* Strike */}
                <th style={{ ...colHead, textAlign: 'center', color: C.text, padding: '4px 12px' }}>Strike</th>
                {/* Puts side */}
                {showType !== 'calls' && <>
                  <th style={{ ...colHead, color: C.redBright }}>Bid</th>
                  <th style={{ ...colHead, color: C.redBright }}>Ask</th>
                  <th style={{ ...colHead, color: C.redBright }}>IV%</th>
                  <th style={{ ...colHead, color: C.redBright }}>OI</th>
                  <th style={{ ...colHead, color: C.redBright }}>Vol</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {chain.calls.map((call, i) => {
                const put = chain.puts.find(p => p.strike === call.strike);
                const isAtm = call.strike === atmStrike;
                const isCurrentCall = currentOcc?.type === 'Call' && currentOcc?.strike === call.strike;
                const isCurrentPut = currentOcc?.type === 'Put' && put && currentOcc?.strike === put.strike;
                const rowBg = isAtm ? `${C.blue}12` : call.itm ? `${C.green}07` : 'transparent';
                return (
                  <tr key={call.strike} style={{ background: rowBg, borderBottom: `1px solid ${C.border}22`, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${C.blue}18`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                    {/* Calls */}
                    {showType !== 'puts' && <>
                      <td style={{ ...cell(null, call.itm ? C.greenBright : C.sub), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(call.sym), 'Call', call.strike)}>{call.vol ?? '—'}</td>
                      <td style={{ ...cell(null, call.itm ? C.greenBright : C.sub), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(call.sym), 'Call', call.strike)}>{call.oi != null ? call.oi.toLocaleString() : '—'}</td>
                      <td style={{ ...cell(null, C.orange), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(call.sym), 'Call', call.strike)}>{call.iv != null ? call.iv + '%' : '—'}</td>
                      <td style={{ ...cell(null, C.greenBright), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(call.sym), 'Call', call.strike)}>{call.ask != null ? '$' + call.ask.toFixed(2) : '—'}</td>
                      <td style={{ ...cell(null, C.green), textAlign: 'right', borderRight: `1px solid ${C.border}44` }} onClick={() => onSelectContract(buildOCC(call.sym), 'Call', call.strike)}>{call.bid != null ? '$' + call.bid.toFixed(2) : '—'}</td>
                    </>}
                    {/* Strike */}
                    <td style={{ textAlign: 'center', padding: '4px 10px', fontWeight: 800, fontSize: 12, color: isAtm ? C.blue : C.text, background: isAtm ? `${C.blue}22` : 'transparent', borderLeft: showType !== 'puts' ? `1px solid ${C.border}44` : 'none', borderRight: showType !== 'calls' ? `1px solid ${C.border}44` : 'none', whiteSpace: 'nowrap' }}>
                      {isAtm && <span style={{ marginRight: 3, fontSize: 9, color: C.blue }}>ATM</span>}
                      ${call.strike}
                      {isCurrentCall && <span style={{ marginLeft: 3, fontSize: 8, color: C.greenBright, fontWeight: 700 }}>★C</span>}
                      {isCurrentPut && <span style={{ marginLeft: 3, fontSize: 8, color: C.redBright, fontWeight: 700 }}>★P</span>}
                    </td>
                    {/* Puts */}
                    {showType !== 'calls' && put ? <>
                      <td style={{ ...cell(null, C.red), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(put.sym), 'Put', put.strike)}>{put.bid != null ? '$' + put.bid.toFixed(2) : '—'}</td>
                      <td style={{ ...cell(null, C.redBright), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(put.sym), 'Put', put.strike)}>{put.ask != null ? '$' + put.ask.toFixed(2) : '—'}</td>
                      <td style={{ ...cell(null, C.orange), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(put.sym), 'Put', put.strike)}>{put.iv != null ? put.iv + '%' : '—'}</td>
                      <td style={{ ...cell(null, put.itm ? C.redBright : C.sub), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(put.sym), 'Put', put.strike)}>{put.oi != null ? put.oi.toLocaleString() : '—'}</td>
                      <td style={{ ...cell(null, put.itm ? C.redBright : C.sub), textAlign: 'right' }} onClick={() => onSelectContract(buildOCC(put.sym), 'Put', put.strike)}>{put.vol ?? '—'}</td>
                    </> : showType !== 'calls' && <td colSpan={5} style={{ color: C.sub, fontSize: 10, textAlign: 'center' }}>—</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────
export default function TradeReplayPanel({ trades, selectedTradeId, themeMode, isMobile, resolveTvSymbol, parseTradeDate, C, cardStyle, inp }) {
  const [interval, setInterval] = useState('5m');
  const [range, setRange] = useState('5d');
  const [view, setView] = useState('underlying'); // 'options' | 'underlying' | 'chain'
  const [optionsData, setOptionsData] = useState(null);
  const [optLoading, setOptLoading] = useState(false);
  const [optError, setOptError] = useState(null);
  const [activeOcc, setActiveOcc] = useState(null); // contract selected from chain

  const trade = (selectedTradeId ? trades.find(t => t.id === selectedTradeId) : null)
    || (trades.length > 0 ? trades[trades.length - 1] : null);

  // Detect options: either market field says 'options' OR asset looks like OCC format
  const tradeOcc = parseOCC(trade?.asset);
  const isOptions = tradeOcc !== null || (trade?.market || '').toLowerCase() === 'options';
  const ticker = tradeOcc?.ticker || (isOptions ? trade?.asset : null);

  // activeOcc: either from chain click or the trade's own contract
  const occ = activeOcc || tradeOcc;

  const underlyingSymbol = resolveTvSymbol(occ?.ticker || ticker || trade?.asset, 'stock');
  const tvSymbol = isOptions ? underlyingSymbol : resolveTvSymbol(trade?.asset, trade?.market);
  const tradeTimestamp = trade?.created_at ? new Date(trade.created_at) : parseTradeDate(trade?.date);
  const pnl = parseFloat(trade?.pnl || 0);
  const chartHeight = Math.max(isMobile ? 420 : 680, (typeof window !== 'undefined' ? window.innerHeight : 900) - 240);

  // Reset activeOcc when trade changes
  useEffect(() => { setActiveOcc(null); }, [trade?.id]);

  // Fetch options OHLCV whenever contract / range changes
  useEffect(() => {
    if (!occ) return;
    if (view !== 'options') return;
    setOptLoading(true); setOptError(null); setOptionsData(null);
    fetch(`/api/news?symbol=${occ.raw}&interval=${interval}&range=${range}`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setOptionsData(d.points); })
      .catch(e => setOptError(e.message))
      .finally(() => setOptLoading(false));
  }, [occ?.raw, interval, range, view]);

  const tvInterval = interval === '1m' ? '1' : interval === '5m' ? '5' : interval === '15m' ? '15' : interval === '30m' ? '30' : interval === '1h' ? '60' : 'D';

  return (
    <div style={{ minWidth: 0 }}>
      {!trade && <div style={{ ...cardStyle, padding: 60, textAlign: 'center', color: C.sub }}>No trades to review.</div>}
      {trade && (
        <div style={{ ...cardStyle, padding: 14, marginBottom: 16 }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <div>
              <div style={{ color: C.text, fontSize: 15, fontWeight: 800, marginBottom: 2 }}>
                {trade.asset}
                {isOptions && occ && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: occ.type === 'Call' ? C.greenBright : C.redBright, background: occ.type === 'Call' ? 'rgba(14,203,129,0.12)' : 'rgba(239,68,68,0.12)', border: `1px solid ${occ.type === 'Call' ? C.green : C.red}55`, borderRadius: 6, padding: '1px 7px' }}>
                    {occ.type}
                  </span>
                )}
              </div>
              <div style={{ color: C.sub, fontSize: 11 }}>{isOptions ? 'Options' : trade.market || 'Stock'} · {trade.strategy || 'No strategy'} · {trade.date}</div>
            </div>
            <div style={{ color: pnl >= 0 ? C.greenBright : C.redBright, fontSize: 16, fontWeight: 900 }}>
              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
            </div>
          </div>

          {/* Options contract details */}
          {isOptions && occ && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, padding: '10px 14px', background: 'rgba(47,140,255,0.07)', border: `1px solid ${C.blue}30`, borderRadius: 10 }}>
              {[['Underlying', occ.ticker, C.text], ['Type', occ.type, occ.type==='Call'?C.greenBright:C.redBright], ['Strike', `$${occ.strike.toFixed(2)}`, C.text], ['Expiry', occ.expiry, C.orange], ['Contract', occ.raw, C.sub]].map(([label, val, color]) => (
                <div key={label} style={{ marginRight: 16 }}>
                  <div style={{ color: C.sub, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                  <div style={{ color, fontSize: label === 'Contract' ? 10 : 13, fontWeight: label === 'Contract' ? 500 : 800, fontFamily: label === 'Contract' ? 'monospace' : 'inherit' }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* No OCC warning */}
          {isOptions && !occ && (
            <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: `1px solid ${C.orange}44`, borderRadius: 8, color: C.orange, fontSize: 11 }}>
              ⚠ Asset <strong>{trade.asset}</strong> isn't in OCC format (e.g. AAPL250117C00150000). Enter the full contract symbol to load options data.
            </div>
          )}

          {/* Controls */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ background: 'rgba(47,140,255,0.12)', border: `1px solid ${C.blue}66`, borderRadius: 12, padding: '4px 10px', color: C.blueBright, fontSize: 11, fontWeight: 700 }}>
              Entry: ${parseFloat(trade.entry || 0).toFixed(2)}{tradeTimestamp && !Number.isNaN(tradeTimestamp.getTime()) ? ` · ${tradeTimestamp.toLocaleString()}` : ''}
            </div>
            <div style={{ background: 'rgba(14,203,129,0.1)', border: `1px solid ${C.green}66`, borderRadius: 12, padding: '4px 10px', color: C.greenBright, fontSize: 11, fontWeight: 700 }}>
              Exit: ${parseFloat(trade.exit || 0).toFixed(2)}
            </div>

            {/* View toggle for options */}
            {isOptions && (
              <div style={{ display: 'flex', borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                {[
                  ['underlying', `📈 ${occ?.ticker || ticker || 'Stock'}`],
                  ...(occ ? [['options','📊 Contract']] : []),
                  ['chain','⛓ Chain'],
                ].map(([v, label]) => (
                  <button key={v} onClick={() => setView(v)} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: view === v ? C.blue : 'transparent', color: view === v ? '#fff' : C.sub }}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Range (options only) */}
            {isOptions && occ && view === 'options' && (
              <select value={range} onChange={e => setRange(e.target.value)} style={{ ...inp, width: 80, padding: '5px 8px', fontSize: 12 }}>
                <option value="1d">1 Day</option>
                <option value="5d">5 Days</option>
                <option value="1mo">1 Month</option>
              </select>
            )}

            {/* Interval */}
            <select value={interval} onChange={e => setInterval(e.target.value)} style={{ ...inp, width: 110, padding: '5px 8px', fontSize: 12, marginLeft: 'auto' }}>
              <option value="1m">1 min</option>
              <option value="5m">5 min</option>
              <option value="15m">15 min</option>
              <option value="30m">30 min</option>
              <option value="1h">1 hour</option>
              <option value="1d">1 day</option>
            </select>
          </div>

          {/* Chart */}
          {(!isOptions || !occ || view === 'underlying') && (
            <div style={{ position: 'relative' }}>
              <TradingViewChart symbol={tvSymbol} interval={tvInterval} themeMode={themeMode} C={C} height={chartHeight} />
              <TradeExecutionOverlay trade={trade} C={C} />
            </div>
          )}

          {isOptions && occ && view === 'options' && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 8px 8px' }}>
              {/* Active contract label (when browsing from chain) */}
              {activeOcc && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'6px 10px', background:`${C.blue}14`, border:`1px solid ${C.blue}33`, borderRadius:8 }}>
                  <span style={{ color:C.sub, fontSize:11 }}>Viewing:</span>
                  <span style={{ color:C.text, fontSize:12, fontWeight:800, fontFamily:'monospace' }}>{activeOcc.raw}</span>
                  <span style={{ color:activeOcc.type==='Call'?C.greenBright:C.redBright, fontSize:11, fontWeight:700 }}>{activeOcc.type} ${activeOcc.strike}</span>
                  <button onClick={() => { setActiveOcc(null); setView('chain'); }} style={{ marginLeft:'auto', padding:'2px 8px', borderRadius:5, border:`1px solid ${C.border}`, background:'transparent', color:C.sub, fontSize:10, cursor:'pointer' }}>← Chain</button>
                </div>
              )}
              {optLoading && (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sub, fontSize: 13 }}>
                  Loading {occ.raw}…
                </div>
              )}
              {optError && (
                <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{ color: C.redBright, fontSize: 13, fontWeight: 700 }}>No data available</div>
                  <div style={{ color: C.sub, fontSize: 12, maxWidth: 400, textAlign: 'center' }}>{optError}</div>
                  <div style={{ color: C.sub, fontSize: 11, marginTop: 4 }}>Contract may be expired or illiquid. Try the chain to pick an active strike.</div>
                  <button onClick={() => setView('chain')} style={{ marginTop:4, padding:'5px 14px', borderRadius:6, border:`1px solid ${C.blue}`, background:`${C.blue}18`, color:C.blue, fontSize:11, fontWeight:700, cursor:'pointer' }}>⛓ Open Chain</button>
                </div>
              )}
              {optionsData && optionsData.length > 0 && (
                <OptionsChart
                  points={optionsData}
                  entry={parseFloat(trade.entry) || null}
                  exit={parseFloat(trade.exit) || null}
                  stop={parseFloat(trade.stopLoss) || null}
                  target={parseFloat(trade.target) || null}
                  C={C}
                  height={chartHeight}
                />
              )}
            </div>
          )}

          {/* Options Chain */}
          {isOptions && view === 'chain' && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 12px' }}>
              <OptionsChain
                ticker={occ?.ticker || ticker}
                currentOcc={occ}
                onSelectContract={(sym, type, strike) => {
                  const parsed = parseOCC(sym);
                  if (parsed) {
                    setActiveOcc(parsed);
                    setView('options');
                  }
                }}
                C={C}
                inp={inp}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
